import { Injectable } from '@angular/core';
import { DialogService } from 'primeng/dynamicdialog';
import { Observable, map, of, switchMap, take, takeUntil, throwIfEmpty } from 'rxjs';

import { FilesService, ServerRegionPick } from '../services/files.service';
import { RegionIoPort } from '@jax-data-science/sci-image-visualizer';
import { RawFileInfo } from '../main/models/file-info';
import { RegionPickerComponent } from '../main/components/region-picker/region-picker.component';

/** Why a server region import was dropped: another image was opened while it was being read. */
export const STALE_REGION_IMPORT = 'Another image was opened while the regions were being read; nothing was imported.';

/**
 * Host-side implementation of the visualization library's {@link RegionIoPort}.
 * Delegates region file I/O to the app's `FilesService`, resolving the selected
 * file internally so the library never sees the app's file types. Bound to the
 * `REGION_IO_PORT` token in `app.module.ts`.
 */
@Injectable({ providedIn: 'root' })
export class RegionIoAdapter implements RegionIoPort {
  constructor(
    private files: FilesService,
    private dialogs: DialogService,
  ) {}

  getSelectedFileName(): string | undefined {
    return this.files.getSelectedFileInfo()?.name;
  }

  roiFileExists(name: string): Observable<boolean> {
    return this.files.roiFileExist(this.files.getSelectedFileInfo(), name);
  }

  saveGeoJson(geoJsonStr: string, filename: string): Observable<void> {
    return this.files.saveGeoJson(geoJsonStr, filename);
  }

  saveSliceGeoJsons(slices: { z: number; geoJsonStr: string }[]): Observable<void> {
    return this.files.saveStackSliceGeoJsons(slices);
  }

  /**
   * The Region Editor's "Import regions from server": the region picker over the project of what
   * is on screen (`FilesService.getShownFileInfo`: the image, or a spatial dataset shown without one;
   * every project when nothing is open), then what was picked resolved for the editor
   * (see `FilesService.resolveRegionPick`). Null when the picker is cancelled.
   *
   * The import is for the image on screen when the picker opens. Resolving the pick (a region set's
   * detection, a folder's listing, the files' downloads) can outlast an image switch, and the
   * editor only takes its import ticket once the pick arrives — it would then import into the image
   * shown at that point. So the resolution stops as soon as another image starts opening, and the
   * pick is checked against the image-open generation before it is handed over: a stale one errors
   * with {@link STALE_REGION_IMPORT}, which the editor shows, and nothing is imported.
   */
  pickServerRegions(): Observable<ServerRegionPick | null> {
    const generation = this.files.getImageOpenGeneration();
    const ref = this.dialogs.open(RegionPickerComponent, {
      header: 'Import regions from server',
      width: '32rem',
      data: { bucket: this.files.getShownFileInfo()?.project?.bucket },
    });
    const stale = () => new Error(STALE_REGION_IMPORT);
    return ref.onClose.pipe(
      take(1),
      switchMap((picked: RawFileInfo[] | undefined) => {
        if (!picked?.length) return of(null);
        if (this.files.getImageOpenGeneration() !== generation) throw stale();
        return this.files.resolveRegionPick(picked).pipe(
          takeUntil(this.files.imageOpened$()),
          throwIfEmpty(stale),
          map((pick) => {
            if (this.files.getImageOpenGeneration() !== generation) throw stale();
            return pick;
          }),
        );
      }),
    );
  }
}
